import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Snackbar from '@mui/material/Snackbar';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { format } from 'date-fns';
import {
  fetchNonPaymentOrders,
  addNonPaymentOrder,
  updateNonPaymentOrder,
  deleteNonPaymentOrder,
  getNonPaymentReasons,
  fetchActiveProviders
} from '../utils/nonPaymentUtils';

function NonPaymentOrders() {
  const [orders, setOrders] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Form state
  const [orderNumber, setOrderNumber] = useState('');
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  
  const reasons = getNonPaymentReasons();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [ordersData, providersData] = await Promise.all([
        fetchNonPaymentOrders(),
        fetchActiveProviders()
      ]);
      
      setOrders(ordersData);
      setProviders(providersData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setOrderNumber('');
    setSelectedProviderId('');
    setReason('');
    setNotes('');
    setEditingId(null);
    setError('');
    setShowForm(false);
  };

  const handleAddClick = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditClick = (order) => {
    setOrderNumber(order.order_number);
    setSelectedProviderId(order.provider_id || '');
    setReason(order.reason);
    setNotes(order.notes || '');
    setEditingId(order.id);
    setError('');
    setShowForm(true);
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;
    
    try {
      setSaving(true);
      await deleteNonPaymentOrder(orderToDelete.id);
      setSnackbarMessage('Order removed from non-payment list');
      setSnackbarOpen(true);
      await loadData();
    } catch (err) {
      console.error('Error deleting order:', err);
      setError(`Failed to delete order: ${err.message}`);
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
    }
  };

  const validateForm = () => {
    if (!orderNumber.trim()) {
      setError('Order number is required');
      return false;
    }
    
    if (!reason) {
      setError('Reason is required');
      return false;
    }
    
    // Check for duplicate order number (excluding current edit)
    const duplicate = orders.find(
      o => o.order_number.toUpperCase() === orderNumber.toUpperCase().trim() && 
           o.id !== editingId
    );
    
    if (duplicate) {
      setError('This order number already exists in the list');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      const orderData = {
        order_number: orderNumber.trim(),
        provider_id: selectedProviderId || null,
        reason: reason,
        notes: notes.trim() || null
      };
      
      if (editingId) {
        await updateNonPaymentOrder(editingId, orderData);
        setSnackbarMessage('Order updated successfully');
      } else {
        await addNonPaymentOrder(orderData);
        setSnackbarMessage('Order added to non-payment list');
      }
      
      setSnackbarOpen(true);
      resetForm();
      await loadData();
    } catch (err) {
      console.error('Error saving order:', err);
      if (err.code === '23505') {
        setError('This order number already exists in the list');
      } else {
        setError(`Failed to save order: ${err.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    resetForm();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            Non-Payment Orders
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={saving}
          >
            Add Order
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage order numbers that should be excluded from contractor payments. 
          These orders will be filtered out during payment calculations but remain visible in results for audit purposes.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {editingId ? 'Edit Order' : 'Add New Order'}
            </Typography>
            
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Order Number"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    disabled={saving}
                    placeholder="e.g., ORD12345"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="provider-label">Provider (Optional)</InputLabel>
                    <Select
                      labelId="provider-label"
                      value={selectedProviderId}
                      label="Provider (Optional)"
                      onChange={(e) => setSelectedProviderId(e.target.value)}
                      disabled={saving}
                    >
                      <MenuItem value="">
                        <em>Any Provider</em>
                      </MenuItem>
                      {providers.map((provider) => (
                        <MenuItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel id="reason-label">Reason</InputLabel>
                    <Select
                      labelId="reason-label"
                      value={reason}
                      label="Reason *"
                      onChange={(e) => setReason(e.target.value)}
                      disabled={saving}
                    >
                      {reasons.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes (Optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={saving}
                    placeholder="Additional context or details..."
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        handleCancel();
                        setShowForm(false);
                      }}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving}
                    >
                      {saving ? <CircularProgress size={24} /> : editingId ? 'Update' : 'Add Order'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Orders Table */}
        <Typography variant="h6" gutterBottom>
          Non-Payment Orders ({orders.length})
        </Typography>

        {orders.length === 0 ? (
          <Alert severity="info">
            No non-payment orders found. Add an order to get started.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Order Number</strong></TableCell>
                  <TableCell><strong>Provider</strong></TableCell>
                  <TableCell><strong>Reason</strong></TableCell>
                  <TableCell><strong>Notes</strong></TableCell>
                  <TableCell><strong>Date Added</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {order.order_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {order.providers ? (
                        <Chip label={order.providers.name} size="small" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Any Provider
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={order.reason} size="small" color="warning" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {order.notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {format(new Date(order.created_at), 'MMM d, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(order)}
                        disabled={saving}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(order)}
                        disabled={saving}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Remove Order from Non-Payment List?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove order <strong>{orderToDelete?.order_number}</strong> from the non-payment list?
            This order will be included in future payment calculations.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
}

export default NonPaymentOrders;
